import classnames from 'classnames';
import _ from 'lodash';
import { Nav, NavItem, NavLink } from 'reactstrap';
import { IconButton, PinIconFilled as pinFilledIcon, PinIcon as pinIcon } from '@/shared/ui';
import { MENU_CONSTANTS } from '@/shared/config';
import ReportHeader, { ReportHeaderProps } from '.';
import useReportHeaderWrapper from './useReportHeaderWrapper';

export type ReportHeaderWrapperProps = ReportHeaderProps & {
  navigateTab: (tab: string) => void;
  isLoadIndexDB?: boolean;
};

/**
 * Top region of the report page, pairing the report header with the tab navigation bar. Forwards every header field straight through and, once data is loaded, renders one nav link per menu constant - disabled until IndexedDB hydration finishes - plus a pin button toggling whether the tab bar stays visible.
 *
 * @param props - The header props, including the fields forwarded to the header, activeTab, navigateTab and isLoadIndexDB.
 * @returns The rendered header block with its tab bar and pin control.
 */
const ReportHeaderWrapper = ({
  isLoadedData,
  title,
  toggleDisplayStudyInfoDrawer,
  toggleDisplayPatientInfoDrawer,
  toggleDisplayFacilityInfoDrawer,
  markArtifactComponent,
  isDisableSaveReport,
  onClickSaveReport,
  isNotEdit,
  profileId,
  studyId,
  isArtifactReport,
  isStartedAiProcessing,
  activeTab,
  isLoadingSaveAiReport,
  undoStateComponent,
  redoStateComponent,
  isEndOfUseReport,
  pendingProfiles,
  isGeneratingReport,
  conflictUsers,
  navigateTab,
  isLoadIndexDB,
}: ReportHeaderWrapperProps) => {
  const { isShowTabHeader, isHoverTabHeader, handleMouseEnter, handleMouseLeave, onClickPin } =
    useReportHeaderWrapper();

  return (
    <div className="top-container" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <ReportHeader
        isLoadedData={isLoadedData}
        title={title}
        conflictUsers={conflictUsers}
        toggleDisplayStudyInfoDrawer={toggleDisplayStudyInfoDrawer}
        toggleDisplayPatientInfoDrawer={toggleDisplayPatientInfoDrawer}
        toggleDisplayFacilityInfoDrawer={toggleDisplayFacilityInfoDrawer}
        markArtifactComponent={markArtifactComponent}
        undoStateComponent={undoStateComponent}
        redoStateComponent={redoStateComponent}
        isDisableSaveReport={isDisableSaveReport}
        onClickSaveReport={onClickSaveReport}
        isLoadingSaveAiReport={isLoadingSaveAiReport}
        isNotEdit={isNotEdit}
        profileId={profileId}
        studyId={studyId}
        isArtifactReport={isArtifactReport}
        isStartedAiProcessing={isStartedAiProcessing}
        activeTab={activeTab}
        isEndOfUseReport={isEndOfUseReport}
        pendingProfiles={pendingProfiles}
        isGeneratingReport={isGeneratingReport}
      />
      {isLoadedData && (
        <div
          className={classnames(
            'top-nav-tabs-container',
            isHoverTabHeader ? '--hover' : '',
            isShowTabHeader ? '--show' : '',
          )}
        >
          <Nav tabs className="top-nav-tabs" aria-label="Report tabs">
            {_.map(MENU_CONSTANTS, (menu) => (
              <NavItem key={menu.keyNumber}>
                <NavLink
                  className={classnames('noselect', activeTab === menu.keyNumber ? 'active' : '')}
                  onClick={() => {
                    navigateTab(menu.keyNumber);
                  }}
                  disabled={!isLoadIndexDB}
                >
                  {menu.title}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <IconButton
            id="pin-tab-btn"
            className="pin-tab-btn"
            iconComponent={<img src={isShowTabHeader ? pinFilledIcon : pinIcon} alt="Pin icon" />}
            onClick={onClickPin}
          />
        </div>
      )}
    </div>
  );
};

export default ReportHeaderWrapper;
